import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Table,
  Badge,
  Button,
  Input,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
  UncontrolledTooltip,
  Collapse,
  Spinner
} from 'reactstrap';
import { useAuth } from '../../../contexts/AuthContext';
import dealService from '../../../service/dealService.ts';
import documentsApi from '../../../service/documentsApi';
import locationService from '../../../service/locationService';
import DocumentSearchList from '../../../components/Documents/DocumentSearchList';

const PreFundingTab = ({ deal }) => {
  const { getToken, user } = useAuth();

  // Accordion states for PreFunding tab
  const [accordionOpen, setAccordionOpen] = useState({
    loanSummary: true,
    paymentPlan: true,
    wireInformation: true
  });

  // Real loan data state
  const [loanData, setLoanData] = useState({
    advanceAmount: 0,
    grossAdvanceAmount: 0,
    netPayback: 0,
    factor: 0,
    monthlyPayment: 0,
    selectionDate: '',
    term: 20 // default installments
  });

  // Loading states
  const [loanDataLoading, setLoanDataLoading] = useState(false);
  const [loanDataError, setLoanDataError] = useState(null);
  const [hasSelection, setHasSelection] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);

  // Configuration state (interactive)
  const [config, setConfig] = useState({
    day: 15,
    installments: loanData.term,
    firstInstallmentDate: ''
  });

  // Payment plan state
  const [paymentPlan, setPaymentPlan] = useState([]);
  const [planGenerated, setPlanGenerated] = useState(false);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [planGenerationError, setPlanGenerationError] = useState(null);

  // Wire section state
  const [wireData, setWireData] = useState({
    status: 'Pending Wire',
    comments: '',
    attachment: null, // Will store {fileName, documentId, details}
    referenceId: ''
  });

  // Track the saved wire status from backend (for readonly logic)
  const [savedWireStatus, setSavedWireStatus] = useState('Pending Wire');
  
  // Track if payment plan has been saved by user
  const [paymentPlanSaved, setPaymentPlanSaved] = useState(false);

  // Modal state
  const [detailModal, setDetailModal] = useState({ isOpen: false, payment: null });
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  
  // Save payment plan state
  const [savingPaymentPlan, setSavingPaymentPlan] = useState(false);
  const [savePaymentPlanError, setSavePaymentPlanError] = useState(null);
  
  // Save wire data state
  const [savingWireData, setSavingWireData] = useState(false);
  const [saveWireDataError, setSaveWireDataError] = useState(null);
  
  // Upload attachment state
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [documentRefreshKey, setDocumentRefreshKey] = useState(0);

  // Loading existing data states
  const [loadingExistingData, setLoadingExistingData] = useState(false);
  const [existingDataError, setExistingDataError] = useState(null);
  const [hasExistingData, setHasExistingData] = useState(false);

  // Status options for installments
  const statusOptions = [
    'Pending to generate',
    'Invoice generated', 
    'Invoice sent',
    'Pending payment',
    'Cancelled'
  ];

  // Toggle accordion sections
  const toggleAccordion = (section) => {
    setAccordionOpen(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Load selected offer data
  const loadSelectedOffer = async (offerId) => {
    try {
      const token = getToken();
      
      if (!token) {
        console.warn('No token available for loading selected offer');
        setSelectedOffer(null);
        return;
      }

      if (!deal || !deal.privateId) {
        console.warn('No deal or privateId available for loading selected offer');
        setSelectedOffer(null);
        return;
      }

      if (!offerId) {
        console.warn('No offerId provided for loading selected offer');
        setSelectedOffer(null);
        return;
      }

      console.log('Loading selected offer:', offerId, 'for deal:', deal.privateId);
      const response = await dealService.getDealOfferById(token, deal.privateId, offerId);
      
      console.log('Selected offer response:', response.data);
      setSelectedOffer(response.data);
      
      // Update loan data with selected offer information
      const offer = response.data;
      setLoanData({
        advanceAmount: offer.advance_amount || offer.advanceAmount || 0,
        grossAdvanceAmount: offer.gross_advance_amount || offer.grossAdvanceAmount || 0,
        netPayback: offer.net_payback || offer.netAdvanceAmount || 0,
        factor: offer.factor || 0,
        monthlyPayment: offer.monthly_payment || offer.monthlyGrossMargin || 0,
        selectionDate: offer.updatedAt || offer.updated_at || offer.createdAt || offer.created_at || '',
        term: offer.term_duration_months || offer.term || 20
      });

      // Update installments in config
      setConfig(prev => ({ 
        ...prev, 
        installments: offer.term_duration_months || offer.term || 20 
      }));

    } catch (err) {
      console.error('Error loading selected offer:', err);
      setLoanDataError('Failed to load selected offer data');
    }
  };

  // Load deal selection status and offer data
  const loadDealSelectionData = async () => {
    if (!deal || !deal.privateId) {
      console.warn('No deal or privateId available');
      return;
    }

    try {
      setLoanDataLoading(true);
      setLoanDataError(null);

      const token = getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      console.log('Checking selection status for deal:', deal.privateId);
      
      const response = await dealService.getDealSelectionStatus(token, deal.privateId);
      
      console.log('Selection status response:', response.data);
      
      if (response.data && response.data.hasSelection) {
        setHasSelection(true);
        
        // Load the selected offer details
        await loadSelectedOffer(response.data.selectedOfferId);
      } else {
        setHasSelection(false);
        setSelectedOffer(null);
        
        // Set default/empty loan data if no selection
        setLoanData({
          advanceAmount: 0,
          grossAdvanceAmount: 0,
          netPayback: 0,
          factor: 0,
          monthlyPayment: 0,
          selectionDate: '',
          term: 20
        });
      }
    } catch (err) {
      console.error('Error loading deal selection data:', err);
      setLoanDataError(err.message || 'Failed to load loan data');
      setHasSelection(false);
      setSelectedOffer(null);
    } finally {
      setLoanDataLoading(false);
    }
  };

  // Load existing prefunding data
  const loadExistingPrefundingData = async () => {
    if (!deal || !deal.privateId) {
      console.warn('No deal or privateId available for loading existing data');
      return;
    }

    try {
      setLoadingExistingData(true);
      setExistingDataError(null);

      const token = getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      console.log('Loading existing prefunding data for deal:', deal.privateId);

      // Make all 3 requests in parallel
      const [prefundingResponse, paymentPlanResponse, wireResponse] = await Promise.allSettled([
        dealService.getPrefundingConfiguration(token, deal.privateId),
        dealService.getPaymentPlan(token, deal.privateId),
        dealService.getWireInformation(token, deal.privateId)
      ]);

      console.log('Prefunding response:', prefundingResponse);
      console.log('Payment plan response:', paymentPlanResponse);
      console.log('Wire response:', wireResponse);

      let hasAnyData = false;

      // Process prefunding configuration
      if (prefundingResponse.status === 'fulfilled' && prefundingResponse.value?.status === 200) {
        const prefundingData = prefundingResponse.value.data;
        console.log('Loading prefunding configuration:', prefundingData);
        
        if (prefundingData) {
          setConfig(prev => ({
            ...prev,
            dayOfMonth: prefundingData.dayOfMonth || prev.dayOfMonth,
            installments: prefundingData.term || prev.installments,
            firstInstallmentDate: prefundingData.firstInstallmentDate || prev.firstInstallmentDate
          }));
          hasAnyData = true;
        }
      }

      // Process payment plan
      if (paymentPlanResponse.status === 'fulfilled' && paymentPlanResponse.value?.status === 200) {
        const paymentPlanData = paymentPlanResponse.value.data;
        console.log('Loading payment plan:', paymentPlanData);
        
        if (paymentPlanData?.installments && Array.isArray(paymentPlanData.installments)) {
          // Map backend data to UI format
          const mappedInstallments = paymentPlanData.installments.map(installment => ({
            number: installment.number,
            date: installment.date,
            amount: installment.amount,
            balance: installment.balanceAfter, // Map balanceAfter to balance for UI
            status: mapBackendEnumToUIStatus(installment.status) // Map backend status to UI status
          }));
          
          setPaymentPlan(mappedInstallments);
          setPlanGenerated(true);
          setPaymentPlanSaved(true); // Mark as saved since it exists in backend
          hasAnyData = true;
        }
      }

      // Process wire information
      if (wireResponse.status === 'fulfilled' && wireResponse.value?.status === 200) {
        const wireData = wireResponse.value.data;
        console.log('Loading wire information:', wireData);
        
        if (wireData) {
          const mappedStatus = mapBackendWireEnumToUIStatus(wireData.status) || 'Pending Wire';
          setWireData(prev => ({
            ...prev,
            status: mappedStatus,
            comments: wireData.comments || prev.comments,
            referenceId: wireData.referenceId || prev.referenceId,
            // Don't load attachment info as it will be shown in DocumentSearchList
          }));
          // Update saved status for readonly logic
          setSavedWireStatus(mappedStatus);
          hasAnyData = true;
        }
      }

      setHasExistingData(hasAnyData);
      
      if (hasAnyData) {
        console.log('Successfully loaded existing prefunding data');
      } else {
        console.log('No existing prefunding data found - showing empty interface');
      }

    } catch (error) {
      console.error('Error loading existing prefunding data:', error);
      setExistingDataError(error.message || 'Failed to load existing data');
      setHasExistingData(false);
    } finally {
      setLoadingExistingData(false);
    }
  };

  // Load deal selection data when component mounts or deal changes
  useEffect(() => {
    loadDealSelectionData();
  }, [deal]);

  // Load existing prefunding data when component mounts or deal changes
  useEffect(() => {
    loadExistingPrefundingData();
  }, [deal]);

  // Initialize suggested first installment date
  useEffect(() => {
    const suggestedDate = calculateSuggestedDate(config.day);
    setConfig(prev => ({ ...prev, firstInstallmentDate: suggestedDate }));
  }, [config.day]);

  // Calculate suggested first installment date
  const calculateSuggestedDate = (day) => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    const currentDay = today.getDate();
    
    let targetMonth = currentMonth;
    let targetYear = currentYear;
    
    // If the day has passed this month, use next month
    if (currentDay >= day) {
      targetMonth += 1;
      if (targetMonth > 11) {
        targetMonth = 0;
        targetYear += 1;
      }
    }
    
    const suggestedDate = new Date(targetYear, targetMonth, day);
    return suggestedDate.toISOString().split('T')[0];
  };

  // Generate payment plan with business days (respecting weekends and holidays)
  const createPaymentPlanWithBusinessDays = (
    { firstDate, installments, monthlyPayment, netPayback },
    holidays = [] // Feriados pre-cargados
  ) => {
    const holidaySet = new Set(holidays);
    const plan = [];
    const startDate = new Date(firstDate + 'T00:00:00');
    const originalDay = startDate.getDate();
    const startMonth = startDate.getMonth();
    const startYear = startDate.getFullYear();
    let remainingBalance = netPayback;
    
    // Función para formatear fecha
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    // Función para verificar si es día hábil
    const isBusinessDay = (dateString) => {
      const date = new Date(dateString + 'T00:00:00');
      const dayOfWeek = date.getDay();
      return dayOfWeek !== 0 && dayOfWeek !== 6 && !holidaySet.has(dateString);
    };
    
    // Función para ajustar a día hábil (local, sin peticiones al backend)
    const adjustToBusinessDay = (dateString) => {
      let currentDate = new Date(dateString + 'T00:00:00');
      let iterations = 0;
      
      while (!isBusinessDay(formatDate(currentDate)) && iterations < 10) {
        currentDate.setDate(currentDate.getDate() + 1);
        iterations++;
      }
      
      return formatDate(currentDate);
    };
    
    for (let i = 0; i < installments; i++) {
      let targetMonth = startMonth + i;
      let targetYear = startYear;
      
      while (targetMonth > 11) {
        targetMonth -= 12;
        targetYear++;
      }
      
      let paymentDay = originalDay;
      const lastDayOfTargetMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
      
      if (originalDay > lastDayOfTargetMonth) {
        paymentDay = lastDayOfTargetMonth;
      }
      
      const year = targetYear;
      const month = String(targetMonth + 1).padStart(2, '0');
      const day = String(paymentDay).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      
      // ✨ Ajustar a día hábil (local, usando holidays pre-cargados)
      const adjustedDate = adjustToBusinessDay(dateString);
      
      const payment = {
        n: i + 1,
        date: adjustedDate,
        amount: monthlyPayment,
        balance: Math.max(0, remainingBalance - monthlyPayment),
        status: 'Pending to generate'
      };
      
      remainingBalance = payment.balance;
      plan.push(payment);
    }
    
    return plan;
  };

  // Generate payment plan using the pure function
  const generatePaymentPlan = async () => {
    if (!deal || !deal.privateId) {
      console.error('Deal privateId is required to generate payment plan');
      return;
    }

    try {
      setGeneratingPlan(true);
      setPlanGenerationError(null);

      const token = getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      // Calculate date range for holidays (ONE TIME ONLY)
      const startDate = config.firstInstallmentDate;
      const endDate = new Date(startDate);
      endDate.setMonth(endDate.getMonth() + config.installments);
      const endDateString = endDate.toISOString().split('T')[0];

      console.log('Loading holidays for date range:', startDate, 'to', endDateString);

      // Step 1: Load ALL holidays for the date range (ONE API CALL)
      const holidays = await locationService.loadHolidays(startDate, endDateString);
      console.log('Loaded holidays:', holidays);

      // Prepare prefunding payload
      const prefundingPayload = {
        advanceAmount: loanData.advanceAmount,
        grossAdvanceAmount: loanData.grossAdvanceAmount,
        netPayback: loanData.netPayback,
        factor: loanData.factor,
        monthlyPayment: loanData.monthlyPayment,
        selectionDate: loanData.selectionDate,
        term: config.installments,
        dayOfMonth: config.day,
        firstInstallmentDate: config.firstInstallmentDate
      };

      console.log('Creating prefunding configuration:', prefundingPayload);

      // Make the prefunding API call
      await dealService.createPrefunding(token, deal.privateId, prefundingPayload);

      console.log('Prefunding configuration created successfully');

      // Step 2: Generate the payment plan with business days (LOCAL, using pre-loaded holidays)
      const plan = createPaymentPlanWithBusinessDays({
        firstDate: config.firstInstallmentDate,
        installments: config.installments,
        monthlyPayment: loanData.monthlyPayment,
        netPayback: loanData.netPayback
      }, holidays);
      
      // Add UI-specific properties
      const planWithIds = plan.map(payment => ({
        ...payment,
        id: payment.n,
        number: payment.n
      }));
      
      setPaymentPlan(planWithIds);
      setPlanGenerated(true);

      console.log('Payment plan generated with business days:', planWithIds);

    } catch (error) {
      console.error('Error generating payment plan:', error);
      setPlanGenerationError(error.message || 'Failed to generate payment plan');
    } finally {
      setGeneratingPlan(false);
    }
  };

  // Update installment status
  const updateInstallmentStatus = (id, newStatus) => {
    setPaymentPlan(prev => 
      prev.map(payment => 
        payment.id === id ? { ...payment, status: newStatus } : payment
      )
    );
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Format number without currency symbol and decimals
  const formatNumber = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      // Parse date manually to avoid timezone issues
      const [year, month, day] = dateString.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'Invoice generated':
      case 'Invoice sent':
        return 'secondary';
      case 'Pending payment':
        return 'light';
      case 'Cancelled':
        return 'outline-dark';
      default:
        return 'outline-secondary';
    }
  };

  // Map backend enum to UI status
  const mapBackendEnumToUIStatus = (backendStatus) => {
    const statusMapping = {
      'PENDING_TO_GENERATE': 'Pending to generate',
      'INVOICE_GENERATED': 'Invoice generated',
      'INVOICE_SENT': 'Invoice sent',
      'PENDING_PAYMENT': 'Pending payment',
      'CANCELLED': 'Cancelled'
    };
    return statusMapping[backendStatus] || 'Pending to generate';
  };

  // Map UI wire status to backend enum
  const mapWireStatusToBackendEnum = (uiStatus) => {
    const statusMapping = {
      'Pending Wire': 'PENDING_WIRE',
      'Wired': 'WIRED'
    };
    return statusMapping[uiStatus] || 'PENDING_WIRE';
  };

  // Map backend wire enum to UI status
  const mapBackendWireEnumToUIStatus = (backendStatus) => {
    const statusMapping = {
      'PENDING_WIRE': 'Pending Wire',
      'WIRED': 'Wired'
    };
    return statusMapping[backendStatus] || 'Pending Wire';
  };

  // Handle file upload with real document service
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!deal || !deal.privateId) {
      console.error('Deal privateId is required for file upload');
      return;
    }

    try {
      setUploadingAttachment(true);
      setUploadError(null);

      const token = getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      console.log('Uploading attachment:', file.name);

      // Step 1: Upload document using documentsApi
      const { documentId, details } = await documentsApi.uploadDocument({
        file,
        category: 'voucher',
        originType: 'voucher',
        originId: deal.publicId,
        description: `Wire attachment for deal ${deal.publicId}`,
        tags: ['wire', 'attachment'],
        onUploadProgress: (progress) => {
          console.log('Upload progress:', progress.percent + '%');
        }
      });

      console.log('File uploaded successfully:', { documentId, details });

      // Step 2: Attach voucher to wire using financial-service
      console.log('Attaching voucher to wire:', documentId);
      await dealService.attachWireVoucher(token, deal.privateId, details);
      console.log('Voucher attached to wire successfully');

      // Update wire data with the uploaded file info
      setWireData(prev => ({ 
        ...prev, 
        attachment: {
          fileName: file.name,
          documentId: documentId,
          details: details
        }
      }));

      // Refresh document list
      setDocumentRefreshKey(prev => prev + 1);

      // Show success message
      setToastMessage('Attachment uploaded and attached successfully!');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);

    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadError(error.message || 'Failed to upload attachment');
    } finally {
      setUploadingAttachment(false);
    }
  };

  // Map UI status to backend enum
  const mapStatusToBackendEnum = (uiStatus) => {
    const statusMapping = {
      'Pending to generate': 'PENDING_TO_GENERATE',
      'Invoice generated': 'INVOICE_GENERATED',
      'Invoice sent': 'INVOICE_SENT',
      'Pending payment': 'PENDING_PAYMENT',
      'Cancelled': 'CANCELLED'
    };
    return statusMapping[uiStatus] || 'PENDING_TO_GENERATE';
  };

  // Handle save payment plan
  const handleSave = async () => {
    if (!deal || !deal.privateId) {
      console.error('Deal privateId is required to save payment plan');
      return;
    }

    if (!paymentPlan || paymentPlan.length === 0) {
      console.error('No payment plan to save');
      return;
    }

    try {
      setSavingPaymentPlan(true);
      setSavePaymentPlanError(null);

      const token = getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      // Prepare payment plan payload
      const paymentPlanPayload = {
        installments: paymentPlan.map(payment => ({
          number: payment.number,
          date: payment.date,
          amount: payment.amount,
          balanceAfter: payment.balance,
          status: mapStatusToBackendEnum(payment.status)
        }))
      };

      console.log('Saving payment plan:', paymentPlanPayload);

      // Make the payment plan API call
      await dealService.savePaymentPlan(token, deal.privateId, paymentPlanPayload);
      // Update deal substatus to PENDING_WIRE
      if(deal.substatus != 'PENDING_WIRE') {
      await dealService.updateDealSubstatus(token, deal.privateId, 'PENDING_WIRE');
      }
      

      console.log('Payment plan saved successfully');
      
      // Mark payment plan as saved
      setPaymentPlanSaved(true);
      
      // Show success message
      setToastMessage('Payment plan saved successfully!');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);

    } catch (error) {
      console.error('Error saving payment plan:', error);
      setSavePaymentPlanError(error.message || 'Failed to save payment plan');
    } finally {
      setSavingPaymentPlan(false);
    }
  };

  // Handle save wire data with payment plan validation
  const handleSaveWireData = async () => {
    if (!deal || !deal.privateId) {
      console.error('Deal privateId is required to save wire data');
      return;
    }

    // Reset error state first
    setSaveWireDataError(null);

    // IMPORTANT: Don't validate payment plan if wire was already saved as "Wired"
    // This prevents re-saving an already completed wire
    if (savedWireStatus === 'Wired') {
      setSaveWireDataError('Wire has already been completed and cannot be modified.');
      return;
    }

    // Verify that payment plan has been saved by user BEFORE starting the save process
    // This validation runs ONLY when trying to SAVE the wire, not when changing the status
    if (!paymentPlanSaved) {
      setSaveWireDataError('You must save the payment plan before saving wire information. Please click "Save" on the payment plan first.');
      return;
    }

    // Additional verification: Verify payment plan exists in backend
    try {
      const token = getToken();
      if (!token) {
        setSaveWireDataError('No authentication token available');
        return;
      }

      // Critical validation: Verify payment plan exists in backend
      const paymentPlanResponse = await dealService.getPaymentPlan(token, deal.privateId);
      if (!paymentPlanResponse.data || !paymentPlanResponse.data.installments || paymentPlanResponse.data.installments.length === 0) {
        setSaveWireDataError('Payment plan must be saved before saving wire information. Please save the payment plan first.');
        return;
      }
      
      console.log('✅ Payment plan verified in backend, proceeding with wire save');
    } catch (verifyError) {
      console.error('Error verifying payment plan:', verifyError);
      setSaveWireDataError('Payment plan must be saved before saving wire information. Please save the payment plan first.');
      return;
    }

    // If we reach here, validation passed - now save the wire data
    try {
      setSavingWireData(true);

      const token = getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      // Prepare wire data payload based on status
      let wirePayload;

      if (wireData.status === 'Wired') {
        // For WIRED status, include wiredDate (current date) and wiredBy (current user)
        const currentDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
        const currentUsername = user?.preferred_username || 'unknown@nlf.com';

        wirePayload = {
          status: mapWireStatusToBackendEnum(wireData.status),
          wiredDate: currentDate,
          wiredBy: currentUsername,
          comments: wireData.comments || '',
          referenceId: wireData.referenceId || ''
        };
      } else {
        // For PENDING_WIRE status, only include basic fields
        wirePayload = {
          status: mapWireStatusToBackendEnum(wireData.status),
          comments: wireData.comments || '',
          referenceId: wireData.referenceId || ''
        };
      }

      console.log('Saving wire data:', wirePayload);

      // Make the wire information API call
      await dealService.updateWireInformation(token, deal.privateId, deal.publicId, wirePayload);

      console.log('Wire information saved successfully');
      
      // Update saved status to match current status (for readonly logic)
      setSavedWireStatus(wireData.status);
      
      // If wire status is now "Wired", update deal status and substatus
      if (wireData.status === 'Wired') {
        try {
          console.log('Wire completed - updating deal status to COLLECTIONS and substatus to FUNDED');
          
          // Update deal status to COLLECTIONS
          await dealService.updateDealStatus(token, deal.privateId, 'COLLECTIONS');
          console.log('Deal status updated to COLLECTIONS successfully');
          
          // Update deal substatus to FUNDED
          await dealService.updateDealSubstatus(token, deal.privateId, 'FUNDED');
          console.log('Deal substatus updated to FUNDED successfully');
          await dealService.updateRemainingAmount(token, deal.privateId, loanData.netPayback);
          console.log('Remaining amount updated to ', loanData.netPayback, ' successfully');
          
        } catch (statusUpdateError) {
          console.error('Error updating deal status/substatus after wire completion:', statusUpdateError);
          // Don't fail the whole wire save process if status update fails
          // Just log the error and continue
        }
      }
      
      // Show success message
      setToastMessage('Wire information saved successfully!');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);

    } catch (error) {
      console.error('Error saving wire data:', error);
      setSaveWireDataError(error.message || 'Failed to save wire information');
    } finally {
      setSavingWireData(false);
    }
  };

  // Check if wire information should be readonly (when saved status is Wired)
  const isWireReadonly = savedWireStatus === 'Wired';

  // Check if wire data is ready to be saved
  const isWireReadyToSave = () => {
    // Wire must be set to "Wired" status
    if (wireData.status !== 'Wired') {
      return false;
    }
    
    // Reference ID must be filled
    if (!wireData.referenceId || wireData.referenceId.trim() === '') {
      return false;
    }
    
    return true;
  };

  return (
    <div>
      {/* Toast notification */}
      {showToast && (
        <Alert color="success" className="position-fixed" style={{ top: '20px', right: '20px', zIndex: 9999 }}>
          <i className="ni ni-check-bold mr-2" />
          {toastMessage}
        </Alert>
      )}

      {/* Loading existing data indicator */}
      {loadingExistingData && (
        <Row className="mb-3">
          <Col>
            <Alert color="info" className="d-flex align-items-center">
              <Spinner size="sm" className="mr-2" />
              Loading existing prefunding data...
            </Alert>
          </Col>
        </Row>
      )}

      {/* Error loading existing data */}
      {existingDataError && (
        <Row className="mb-3">
          <Col>
            <Alert color="warning">
              <i className="ni ni-support-16 mr-2" />
              Warning: Could not load existing data. {existingDataError}
            </Alert>
          </Col>
        </Row>
      )}

    <Row>
      <Col>

          {/* Card 1 - Loan Summary (read-only) */}
          <Card className="shadow-sm mb-3">
            <CardHeader 
              className="d-flex justify-content-between align-items-center cursor-pointer"
              onClick={() => toggleAccordion('loanSummary')}
              style={{ cursor: 'pointer', backgroundColor: accordionOpen.loanSummary ? '#f8f9fa' : '#fff' }}
            >
              <div className="d-flex align-items-center">
                <i className="ni ni-chart-bar-32 text-primary mr-2" />
                <h6 className="mb-0 text-muted font-weight-bold">LOAN SUMMARY</h6>
              </div>
              <div className="d-flex align-items-center">
                <Badge color="light" className="text-dark mr-2">
                  Deal #{deal?.publicId || 'N/A'}
                </Badge>
                <Button 
                  color="link" 
                  size="sm" 
                  className="p-0"
                >
                  <i className={`ni ${accordionOpen.loanSummary ? 'ni-fat-remove' : 'ni-fat-add'}`} 
                    style={{ fontSize: '1.0rem' }}
                  />
                </Button>
              </div>
            </CardHeader>
            <Collapse isOpen={accordionOpen.loanSummary}>
              <CardBody>
                {/* Loading State */}
                {loanDataLoading && (
                  <div className="text-center py-4">
                    <Spinner color="secondary" />
                    <p className="mt-2 mb-0 text-muted">Loading loan data...</p>
                  </div>
                )}

                {/* Error State */}
                {loanDataError && !loanDataLoading && (
                  <Alert color="danger" className="mb-3">
                    <i className="ni ni-support-16 mr-2" />
                    {loanDataError}
                    <Button 
                      color="outline-danger" 
                      size="sm" 
                      className="ml-2"
                      onClick={loadDealSelectionData}
                    >
                      Retry
                    </Button>
                  </Alert>
                )}

                {/* No Selection State */}
                {!loanDataLoading && !loanDataError && !hasSelection && (
                  <Alert color="info" className="mb-3">
                    <i className="ni ni-bell-55 mr-2" />
                    No offer has been selected yet. Loan summary will be available after offer selection.
                  </Alert>
                )}

                {/* Loan Data */}
                {!loanDataLoading && !loanDataError && (
              <div className="p-2">
                <Row>
                  <Col md="6" className="mb-1">
                    <div className="d-flex align-items-center" style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '4px', marginBottom: '4px' }}>
                      <label className="form-label text-muted mb-0" style={{ fontSize: '0.8rem', fontWeight: '500', minWidth: '120px' }}>Advance Amount</label>
                      <div className="text-dark ml-3" style={{ fontSize: '0.8rem', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                        {formatNumber(loanData.advanceAmount)}
                      </div>
                    </div>
                  </Col>
                  <Col md="6" className="mb-1">
                    <div className="d-flex align-items-center" style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '4px', marginBottom: '4px' }}>
                      <label className="form-label text-muted mb-0" style={{ fontSize: '0.8rem', fontWeight: '500', minWidth: '120px' }}>Gross Advance Amount</label>
                      <div className="text-dark ml-3" style={{ fontSize: '0.8rem', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                        {formatNumber(loanData.grossAdvanceAmount)}
                      </div>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md="6" className="mb-1">
                    <div className="d-flex align-items-center" style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '4px', marginBottom: '4px' }}>
                      <label className="form-label text-muted mb-0" style={{ fontSize: '0.8rem', fontWeight: '500', minWidth: '120px' }}>Payback</label>
                      <div className="text-dark ml-3" style={{ fontSize: '0.8rem', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                        {formatNumber(loanData.netPayback)}
                      </div>
                    </div>
                  </Col>
                  <Col md="6" className="mb-1">
                    <div className="d-flex align-items-center" style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '4px', marginBottom: '4px' }}>
                      <label className="form-label text-muted mb-0" style={{ fontSize: '0.8rem', fontWeight: '500', minWidth: '120px' }}>Factor</label>
                      <div className="text-dark ml-3" style={{ fontSize: '0.8rem', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                        {loanData.factor}
                      </div>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md="6" className="mb-1">
                    <div className="d-flex align-items-center" style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '4px', marginBottom: '4px' }}>
                      <label className="form-label text-muted mb-0" style={{ fontSize: '0.8rem', fontWeight: '500', minWidth: '120px' }}>Monthly Payment</label>
                      <div className="text-dark ml-3" style={{ fontSize: '0.8rem', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                        {formatNumber(loanData.monthlyPayment)}
                      </div>
                    </div>
                  </Col>
                  <Col md="6" className="mb-1">
                    <div className="d-flex align-items-center" style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '4px', marginBottom: '4px' }}>
                      <label className="form-label text-muted mb-0" style={{ fontSize: '0.8rem', fontWeight: '500', minWidth: '120px' }}>Selection Date</label>
                      <div className="text-dark ml-3" style={{ fontSize: '0.8rem', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                        {formatDate(loanData.selectionDate)}
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
                )}
              </CardBody>
            </Collapse>
          </Card>

          {/* Card 2 - Payment Plan (Combined Collection Configuration + Payment Plan) */}
          <Card className="shadow-sm mb-4">
            <CardHeader 
              className="d-flex justify-content-between align-items-center cursor-pointer"
              onClick={() => toggleAccordion('paymentPlan')}
              style={{ cursor: 'pointer', backgroundColor: accordionOpen.paymentPlan ? '#f8f9fa' : '#fff' }}
            >
              <div className="d-flex align-items-center">
                <i className="ni ni-calendar-grid-58 text-primary mr-2" />
                <h6 className="mb-0 text-muted font-weight-bold">PAYMENT PLAN</h6>
                {paymentPlan.length > 0 && (
                  <Badge color="light" className="ml-2 text-dark">
                    {paymentPlan.length} installments
                  </Badge>
                )}
              </div>
              <div className="d-flex align-items-center">
                {accordionOpen.paymentPlan && planGenerated && (
                  <Button
                    color="outline-secondary"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent accordion toggle
                      handleSave();
                    }}
                          disabled={savingPaymentPlan || savedWireStatus === 'Wired'}
                    className="mr-2"
                  >
                    {savingPaymentPlan ? (
                      <>
                        <Spinner size="sm" className="mr-1" />
                        Saving...
                      </>
                    ) : savedWireStatus === 'Wired' ? (
                      <>
                        <i className="ni ni-lock-circle-open mr-1" />
                        Locked
                      </>
                    ) : (
                      <>
                        <i className="ni ni-check-bold mr-1" />
                        Save
                      </>
                    )}
                  </Button>
                )}
                <Button
                  color="link"
                  size="sm"
                  className="p-0"
                >
                  <i className={`ni ${accordionOpen.paymentPlan ? 'ni-fat-remove' : 'ni-fat-add'}`}
                    style={{ fontSize: '1.0rem' }}
                  />
                </Button>
              </div>
            </CardHeader>
            <Collapse isOpen={accordionOpen.paymentPlan}>
              <CardBody>
                {/* Collection Configuration Section */}
                <div className="mb-1">
                  <h6 className="text-muted small font-weight-bold mb-2">
                    <i className="ni ni-settings-gear-65 mr-2" />
                    COLLECTION CONFIGURATION
                  </h6>
                  
                  {/* Wire Completed State - Only show when wire is SAVED as Wired */}
                  {savedWireStatus === 'Wired' && (
                    <Alert color="info" className="mb-3">
                      <i className="ni ni-lock-circle-open mr-2" />
                      <strong>Wire Completed - Plan Locked</strong><br/>
                      The wire has been completed and the payment plan is now locked. You cannot generate a new plan or modify the existing one. Proceed to the Collections tab to manage payments.
                    </Alert>
                  )}

                  {/* Error State for Plan Generation */}
                  {planGenerationError && (
                    <Alert color="danger" className="mb-3">
                      <i className="ni ni-support-16 mr-2" />
                      {planGenerationError}
                    </Alert>
                  )}
                  
                  <Row className="align-items-end">
                    <Col lg="3" md="6" className="mb-2">
                      <FormGroup>
                        <Label className="form-control-label text-muted small font-weight-bold">
                          DAY (1-30)
                        </Label>
                        <Input
                          type="select"
                          value={config.day}
                          onChange={(e) => setConfig(prev => ({ ...prev, day: parseInt(e.target.value) }))}
                          aria-label="Select collection day"
                          size="sm"
                          disabled={savedWireStatus === 'Wired'}
                        >
                          {Array.from({ length: 30 }, (_, i) => (
                            <option key={i + 1} value={i + 1}>{i + 1}</option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col lg="3" md="6" className="mb-2">
                      <FormGroup>
                        <Label className="form-control-label text-muted small font-weight-bold">
                          INSTALLMENTS
                        </Label>
                        <Input
                          type="text"
                          value={config.installments}
                          readOnly
                          className="bg-light"
                          aria-label="Number of installments"
                          size="sm"
                        />
                      </FormGroup>
                    </Col>
                    <Col lg="4" md="8" className="mb-2">
                      <FormGroup>
                        <Label className="form-control-label text-muted small font-weight-bold">
                          FIRST INSTALLMENT DATE
                        </Label>
                        <small className="text-muted d-block mb-1">
                          <i className="ni ni-calendar-grid-58 mr-1" />
                          Only future dates are allowed
                        </small>
                        <Input
                          type="date"
                          value={config.firstInstallmentDate}
                          min={new Date().toISOString().split('T')[0]} // Only allow today and future dates
                          onChange={(e) => {
                            const selectedDate = e.target.value;
                            if (selectedDate) {
                              // Validate that the selected date is not in the past
                              const today = new Date();
                              const selectedDateObj = new Date(selectedDate + 'T00:00:00');
                              
                              if (selectedDateObj < today) {
                                // If date is in the past, don't update and show error
                                console.warn('Cannot select a date in the past for payment plan');
                                return;
                              }
                              
                              // Extract day from the selected date
                              const dayFromDate = selectedDateObj.getDate();
                              
                              // Update both firstInstallmentDate and day
                              setConfig(prev => ({ 
                                ...prev, 
                                firstInstallmentDate: selectedDate,
                                day: dayFromDate
                              }));
                            } else {
                              // If date is cleared, only update firstInstallmentDate
                              setConfig(prev => ({ ...prev, firstInstallmentDate: selectedDate }));
                            }
                          }}
                          aria-label="First installment date"
                          size="sm"
                          disabled={savedWireStatus === 'Wired'}
                        />
                      </FormGroup>
                    </Col>
                    <Col lg="2" md="4" className="mb-2">
                      <FormGroup>
                        <Button
                          color="outline-secondary"
                          onClick={generatePaymentPlan}
                          block
                          disabled={generatingPlan || savedWireStatus === 'Wired'}
                          aria-label="Generate payment plan"
                          className="d-block d-md-inline-block"
                          size="sm"
                        >
                          {generatingPlan ? (
                            <>
                              <Spinner size="sm" className="mr-1" />
                              <span className="d-none d-sm-inline">Generating...</span>
                              <span className="d-inline d-sm-none">...</span>
                            </>
                          ) : savedWireStatus === 'Wired' ? (
                            <>
                              <i className="ni ni-lock-circle-open mr-1" />
                              <span className="d-none d-sm-inline">Wire Completed</span>
                              <span className="d-inline d-sm-none">Completed</span>
                            </>
                          ) : (
                            <>
                              <i className="ni ni-settings-gear-65 mr-1" />
                              <span className="d-none d-sm-inline">Generate Plan</span>
                              <span className="d-inline d-sm-none">Generate</span>
                            </>
                          )}
                        </Button>
                      </FormGroup>
                    </Col>
                  </Row>
                </div>

                {/* Payment Plan Table Section */}
                {planGenerated && (
                  <div>
                    <h6 className="text-muted small font-weight-bold mb-2">
                      <i className="ni ni-calendar-grid-58 mr-2" />
                      PAYMENT SCHEDULE
                    </h6>
                    
                    {/* Error State for Payment Plan Save */}
                    {savePaymentPlanError && (
                      <Alert color="danger" className="mb-3">
                        <i className="ni ni-support-16 mr-2" />
                        {savePaymentPlanError}
                      </Alert>
                    )}
                    
                    {/* Desktop Table View */}
                    <div className="d-none d-md-block mt-3" style={{ overflowX: 'auto' }}>
                      <div
                        className="shadow-sm rounded bg-white p-3"
                        style={{
                          maxWidth: '600px',
                        }}
                      >
                        <Table
                          className="align-items-center table-flush mb-0 table-sm text-center"
                          responsive
                        >
                          <thead className="thead-light">
                            <tr>
                              <th scope="col" className="py-2 px-2 text-center">#</th>
                              <th scope="col" className="py-2 px-2 text-center">Date</th>
                              <th scope="col" className="py-2 px-2 text-center">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paymentPlan.map((payment) => (
                              <tr key={payment.id}>
                                <td className="py-2 px-2 text-center">
                                  <Badge color="light" className="badge-circle badge-sm">
                                    {payment.number}
                                  </Badge>
                                </td>
                                <td className="py-2 px-2 text-center">
                                  <span className="text-sm font-weight-bold">
                                    {formatDate(payment.date)}
                                  </span>
                                </td>
                                <td className="py-2 px-2 text-center">
                                  <span className="font-weight-bold text-dark">
                                    {formatNumber(payment.amount)}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </div>

                    {/* Mobile Card View */}
                    <div className="d-block d-md-none">
                      {paymentPlan.map((payment) => (
                        <Card key={payment.id} className="mb-2 border">
                          <CardBody className="p-2">
                            <div className="d-flex justify-content-between align-items-start mb-1">
                              <div className="d-flex align-items-center">
                                <Badge color="light" className="badge-circle badge-sm mr-2">
                                  {payment.number}
                                </Badge>
                                <span className="font-weight-bold text-sm">
                                  {formatDate(payment.date)}
                                </span>
                              </div>
                            </div>
                            
                            <div>
                              <small className="text-muted font-weight-bold">AMOUNT</small>
                              <div className="font-weight-bold text-dark">
                                {formatNumber(payment.amount)}
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </CardBody>
            </Collapse>
          </Card>

          {/* Card 3 - Wire Section */}
          <Card className="shadow-sm mb-4">
            <CardHeader 
              className="d-flex justify-content-between align-items-center cursor-pointer"
              onClick={() => toggleAccordion('wireInformation')}
              style={{ cursor: 'pointer', backgroundColor: accordionOpen.wireInformation ? '#f8f9fa' : '#fff' }}
            >
              <div className="d-flex align-items-center">
                <i className="ni ni-send text-primary mr-2" />
                <h6 className="mb-0 text-muted font-weight-bold">WIRE INFORMATION</h6>
                <Badge 
                  color={wireData.status === 'Wired' ? 'secondary' : 'light'} 
                  className={`ml-2 ${wireData.status === 'Wired' ? 'text-white' : 'text-dark'}`}
                >
                  {wireData.status}
                </Badge>
              </div>
              <div className="d-flex align-items-center">
                {accordionOpen.wireInformation && !isWireReadonly && isWireReadyToSave() && (
                  <Button
                    color="outline-secondary"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent accordion toggle
                      handleSaveWireData();
                    }}
                    disabled={savingWireData || savedWireStatus === 'Wired'}
                    className="mr-2"
                  >
                    {savingWireData ? (
                      <>
                        <Spinner size="sm" className="mr-1" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="ni ni-check-bold mr-1" />
                        Save
                      </>
                    )}
                  </Button>
                )}
                <Button 
                  color="link" 
                  size="sm" 
                  className="p-0"
                >
                  <i className={`ni ${accordionOpen.wireInformation ? 'ni-fat-remove' : 'ni-fat-add'}`} 
                    style={{ fontSize: '1.0rem' }}
                  />
                </Button>
            </div>
          </CardHeader>
            <Collapse isOpen={accordionOpen.wireInformation}>
              <CardBody>
                {/* Error State for Wire Data Save */}
                {saveWireDataError && (
                  <Alert color="danger" className="mb-3">
                    <i className="ni ni-support-16 mr-2" />
                    {saveWireDataError}
                  </Alert>
                )}
                
              <Row>
                <Col lg="6" className="mb-3">
                  <FormGroup>
                    <Label className="form-control-label text-muted small font-weight-bold">
                      STATUS
                    </Label>
                    <Input
                      type="select"
                      value={wireData.status}
                      onChange={(e) => setWireData(prev => ({ ...prev, status: e.target.value }))}
                      aria-label="Wire status"
                      disabled={isWireReadonly}
                    >
                      <option value="Pending Wire">Pending Wire</option>
                      <option value="Wired">Wired</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col lg="6" className="mb-3">
                  <FormGroup>
                    <Label className="form-control-label text-muted small font-weight-bold">
                      REFERENCE ID
                    </Label>
                    <Input
                      type="text"
                      value={wireData.referenceId}
                      onChange={(e) => setWireData(prev => ({ ...prev, referenceId: e.target.value }))}
                      placeholder="Enter reference ID"
                      aria-label="Reference ID"
                      readOnly={isWireReadonly}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col lg="6" className="mb-3">
                  <FormGroup>
                    <Label className="form-control-label text-muted small font-weight-bold">
                      COMMENTS
                    </Label>
                    <Input
                      type="textarea"
                      rows="3"
                      value={wireData.comments}
                      onChange={(e) => setWireData(prev => ({ ...prev, comments: e.target.value }))}
                      placeholder="Enter comments"
                      aria-label="Comments"
                      readOnly={isWireReadonly}
                    />
                  </FormGroup>
                </Col>
                <Col lg="6" className="mb-3">
                  <FormGroup>
                    <Label className="form-control-label text-muted small font-weight-bold">
                      ATTACHMENT
                    </Label>
                    
                    {/* Upload Error */}
                    {uploadError && (
                      <Alert color="danger" className="mb-2">
                        <i className="ni ni-support-16 mr-2" />
                        {uploadError}
                      </Alert>
                    )}
                    
                    {!isWireReadonly && (
                      <div>
                        <input
                          type="file"
                          id="wireAttachment"
                          onChange={handleFileUpload}
                          style={{ display: 'none' }}
                          aria-label="Upload attachment"
                          disabled={uploadingAttachment}
                          accept=".pdf,.png,.jpg,.jpeg,.gif,.doc,.docx,.xls,.xlsx,.txt,.csv"
                        />
                        <Button
                          color="primary"
                          size="sm"
                          onClick={() => document.getElementById('wireAttachment').click()}
                          disabled={uploadingAttachment}
                        >
                          {uploadingAttachment ? (
                            <>
                              <Spinner size="sm" className="mr-1" />
                              Uploading...
                            </>
                          ) : (
                            'Upload'
                          )}
                        </Button>
                      </div>
                    )}

                    {isWireReadonly && (
                      <div className="text-muted">
                        <i className="ni ni-lock-circle-open mr-2" />
                        Upload disabled - Wire status is completed
                      </div>
                    )}
                    
                    {/* Attachment Info */}
                    {wireData.attachment && (
                      <div className="mt-2 p-2 border rounded bg-light">
                        <small className="text-success d-block">
                          <i className="ni ni-check-bold mr-1" />
                          File uploaded successfully
                        </small>
                        <small className="text-muted d-block mt-1">
                          <i className="ni ni-paper-diploma mr-1" />
                          {wireData.attachment.fileName}
                        </small>
                        {wireData.attachment.documentId && (
                          <small className="text-muted d-block">
                            Document ID: {wireData.attachment.documentId}
                          </small>
                        )}
            </div>
                    )}
                  </FormGroup>
                </Col>
              </Row>

          </CardBody>
            </Collapse>
        </Card>
      </Col>
    </Row>

      {/* Detail Modal */}
      <Modal 
        isOpen={detailModal.isOpen} 
        toggle={() => setDetailModal({ isOpen: false, payment: null })}
        size="md"
        className="modal-dialog-centered"
      >
        <ModalHeader toggle={() => setDetailModal({ isOpen: false, payment: null })}>
          <span className="d-none d-sm-inline">Payment Details #{detailModal.payment?.number}</span>
          <span className="d-inline d-sm-none">Payment #{detailModal.payment?.number}</span>
        </ModalHeader>
        <ModalBody>
          {detailModal.payment && (
            <div>
              <Row className="mb-2">
                <Col xs="5" sm="6">
                  <strong>Date:</strong>
                </Col>
                <Col xs="7" sm="6">
                  {formatDate(detailModal.payment.date)}
                </Col>
              </Row>
              <Row className="mb-2">
                <Col xs="5" sm="6">
                  <strong>Amount:</strong>
                </Col>
                <Col xs="7" sm="6">
                  {formatCurrency(detailModal.payment.amount)}
                </Col>
              </Row>
              <Row className="mb-2">
                <Col xs="5" sm="6">
                  <strong>Balance:</strong>
                </Col>
                <Col xs="7" sm="6">
                  {formatCurrency(detailModal.payment.balance)}
                </Col>
              </Row>
              <Row className="mb-2">
                <Col xs="5" sm="6">
                  <strong>Status:</strong>
                </Col>
                <Col xs="7" sm="6">
                  <Badge color={getStatusBadgeColor(detailModal.payment.status)}>
                    {detailModal.payment.status}
                  </Badge>
                </Col>
              </Row>
            </div>
          )}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-center justify-content-sm-end">
          <Button 
            color="outline-secondary" 
            onClick={() => setDetailModal({ isOpen: false, payment: null })}
            block={window.innerWidth < 576}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

/**
 * Load holidays from backend for a date range
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {Promise<Array>} Array of holiday dates
 */
export const loadHolidays = async (startDate, endDate) => {
  try {
    const response = await fetch(
      `/param-service/api/params/holidays/dates?start=${startDate}&end=${endDate}`
    );
    if (!response.ok) {
      throw new Error(`Failed to load holidays: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error loading holidays:', error);
    return []; // Return empty array if holidays can't be loaded
  }
};

/**
 * Pure function to generate payment plan rows (exported for reuse)
 * @param {Object} params - Payment plan parameters
 * @param {string} params.firstDate - First installment date in YYYY-MM-DD format
 * @param {number} params.installments - Number of installments
 * @param {number} params.monthlyPayment - Monthly payment amount
 * @param {number} params.netPayback - Total net payback amount
 * @returns {Array} Array of payment plan rows with { n, date, amount, balance, status }
 */
export const createPaymentPlan = ({ firstDate, installments, monthlyPayment, netPayback }) => {
  const plan = [];
  const startDate = new Date(firstDate + 'T00:00:00'); // Force local timezone
  const originalDay = startDate.getDate();
  const startMonth = startDate.getMonth();
  const startYear = startDate.getFullYear();
  let remainingBalance = netPayback;
  
  for (let i = 0; i < installments; i++) {
    // Calculate target month and year
    let targetMonth = startMonth + i;
    let targetYear = startYear;
    
    // Handle year overflow
    while (targetMonth > 11) {
      targetMonth -= 12;
      targetYear++;
    }
    
    // Determine the correct day for this month
    let paymentDay = originalDay;
    
    // Get the last day of the target month
    const lastDayOfTargetMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
    
    // If the original day doesn't exist in this month, use the last day
    if (originalDay > lastDayOfTargetMonth) {
      paymentDay = lastDayOfTargetMonth;
    }
    
    // Create the date string directly without using Date constructor
    const year = targetYear;
    const month = String(targetMonth + 1).padStart(2, '0');
    const day = String(paymentDay).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    
    const payment = {
      n: i + 1,
      date: dateString,
      amount: monthlyPayment,
      balance: Math.max(0, remainingBalance - monthlyPayment),
      status: 'Pending to generate'
    };
    
    remainingBalance = payment.balance;
    plan.push(payment);
  }
  
  return plan;
};

/**
 * Generate payment plan with business days (respecting weekends and holidays)
 * @param {Object} params - Payment plan parameters
 * @param {string} params.firstDate - First installment date in YYYY-MM-DD format
 * @param {number} params.installments - Number of installments
 * @param {number} params.monthlyPayment - Monthly payment amount
 * @param {number} params.netPayback - Total net payback amount
 * @param {Array} holidays - Array of holiday dates in YYYY-MM-DD format
 * @returns {Array} Array of payment plan rows with { n, date, amount, balance, status }
 */
export const createPaymentPlanWithBusinessDays = (
  { firstDate, installments, monthlyPayment, netPayback },
  holidays = [] // Feriados pre-cargados
) => {
  const holidaySet = new Set(holidays);
  const plan = [];
  const startDate = new Date(firstDate + 'T00:00:00');
  const originalDay = startDate.getDate();
  const startMonth = startDate.getMonth();
  const startYear = startDate.getFullYear();
  let remainingBalance = netPayback;
  
  // Función para formatear fecha
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Función para verificar si es día hábil
  const isBusinessDay = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    const dayOfWeek = date.getDay();
    return dayOfWeek !== 0 && dayOfWeek !== 6 && !holidaySet.has(dateString);
  };
  
  // Función para ajustar a día hábil
  const adjustToBusinessDay = (dateString) => {
    let currentDate = new Date(dateString + 'T00:00:00');
    let iterations = 0;
    
    while (!isBusinessDay(formatDate(currentDate)) && iterations < 10) {
      currentDate.setDate(currentDate.getDate() + 1);
      iterations++;
    }
    
    return formatDate(currentDate);
  };
  
  for (let i = 0; i < installments; i++) {
    let targetMonth = startMonth + i;
    let targetYear = startYear;
    
    while (targetMonth > 11) {
      targetMonth -= 12;
      targetYear++;
    }
    
    let paymentDay = originalDay;
    const lastDayOfTargetMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
    
    if (originalDay > lastDayOfTargetMonth) {
      paymentDay = lastDayOfTargetMonth;
    }
    
    const year = targetYear;
    const month = String(targetMonth + 1).padStart(2, '0');
    const day = String(paymentDay).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    
    // ✨ Ajustar a día hábil
    const adjustedDate = adjustToBusinessDay(dateString);
    
    const payment = {
      n: i + 1,
      date: adjustedDate,
      amount: monthlyPayment,
      balance: Math.max(0, remainingBalance - monthlyPayment),
      status: 'Pending to generate'
    };
    
    remainingBalance = payment.balance;
    plan.push(payment);
  }
  
  return plan;
};

export default PreFundingTab;
